import type {
	EditorActionName,
	EditorActionPayload,
	ExportedFunctions,
	LoadingSnapshot,
	PolygonAppController,
} from "@polygonjs/js-core";
import { resolvePolygonRuntime, type PolygonCoreModule } from "./polygonRuntime";
import { registerEngineErrorSink, reportHostError } from "./errorReporting";
import {
	registerLiveApiRelay,
	disconnectLiveApiRelay,
	type LiveApiRelayHost,
} from "./liveApiRelay";
import { startWebAppExport } from "./webAppExport";

// The web editor / runtime bootstrap (PLAN-web-editor-save-load W1/W2). The engine loads the
// scene inline (sceneData + sceneName) at boot and PUSHES editor→host actions via onEditorAction
// (Ctrl+S save, File→Save As, File→Dashboard). The host (this file) does the persistence /
// navigation — the engine has no knowledge of auth/transport.
//
// NOTE: pass a plainly-typed options object (no `as any`) so TypeScript checks it against
// AppInitOptions. That's what catches a renamed/removed callback.
//
// EVERY js-core IMPORT HERE IS `import type` (PLAN-per-version-js-core.md). The VALUES
// (`polygonAppInit`, `internalApi`, the loaders, the embed entry point) come from the bundle that
// matches THIS SCENE's engine version, resolved at runtime by polygonRuntime.ts from
// /polygon-js/<version>/. A scene is always loaded with the version it was saved with, and a single
// pinned js-core driving all of them is a silent mismatch: the wasm's `env` import object is a Proxy
// that stubs any name js-core lacks, and a renamed export reads back `undefined` with nothing logged.
// `@polygonjs/js-core` stays in package.json as a TYPES-ONLY devDependency, pinned to the oldest
// hosted version, so the types this file checks against are ones every served engine really has.

// The server's answer to "what may this visitor do on the web", as the scenes page injects it.
// Exported because the entrypoint parses the attribute and needs the shape to parse it into.
export interface EntitlementsOption {
	tier: string;
	allows: string[];
}

// Read the injected grant off a data-attribute. Anything unreadable is the FREE grant with nothing
// allowed: a page whose JSON did not parse must not become a page where everything is on. The
// attribute is absent on any page that predates it, which is the same case.
export function parseEntitlements(raw: string | undefined): EntitlementsOption {
	const closed: EntitlementsOption = { tier: "free", allows: [] };
	if (!raw) return closed;
	try {
		const parsed = JSON.parse(raw) as Partial<EntitlementsOption>;
		if (typeof parsed?.tier !== "string" || !Array.isArray(parsed?.allows)) return closed;
		return { tier: parsed.tier, allows: parsed.allows.filter((a) => typeof a === "string") };
	} catch {
		console.warn("[editor] could not parse data-entitlements; assuming the free tier");
		return closed;
	}
}

export interface LoadEditorOptions {
	domElement: HTMLElement;
	sceneData: string; // inline `.poly` (empty for a new scene)
	sceneName: string; // seeded name (empty ⇒ engine treats it as new and prompts on save)
	// One of the examples the editor SHIPS, by its ExampleId name ("RBD_SIMULATION"). The engine parks
	// it in the pre-scene window and builds it on the first frame that has an editor and a scene, as
	// one undo step, exactly as clicking it in the editor's own Examples window does. So the page
	// boots an EMPTY scene and the example arrives into it; there is no `.poly` involved and nothing is
	// fetched. Used by /examples/<slug>; undefined everywhere else.
	exampleId?: string;
	polygonVersion: string;
	mode: "edit" | "play";
	// Start the scene PLAYING once it has loaded, instead of leaving it on frame 0. Only meaningful
	// with `sceneData`: the engine reads it where it applies those bytes. Used by a blog post whose
	// `polygon-scene` block says `autoplay: true`. An embedded scene offers the reader no transport
	// controls, so the post is the only thing that can ask for time to be running. Defaults to false.
	autoplay?: boolean;
	// Boot with NO editor at all, on this same editor build: the page gets a bare viewer. Set by a
	// page showing SEVERAL scenes, because only one of them could hold the single editor and one
	// figure among several wearing editor chrome reads as a fault rather than a feature. A
	// single-scene page leaves it off and keeps the editor it has always had.
	editorDisabled?: boolean;
	// Whether the engine owns the whole window. True for the editor pages, where the canvas is
	// position:fixed and sized to the viewport. FALSE for a scene embedded in a blog post: the canvas
	// then sizes itself to `domElement` through a ResizeObserver, which is also what makes expanding
	// that element to full-screen work with no further wiring.
	fullscreen?: boolean;
	saveUrl?: string; // existing scene: PATCH here (SAVE_SCENE), no reload
	createUrl?: string; // POST here for a new scene / SAVE_SCENE_AS, then navigate
	scenesNewUrl?: string; // SCENE_NEW target (defaults to /scenes/new)
	sceneEditUrl?: string; // OPEN_EDIT (File→Edit) target: the scene settings page
	dashboardUrl?: string; // OPEN_DASHBOARD target (defaults to /dashboard)
	// ACCOUNT_SIGN_IN target (defaults to /session/new). The editor appends a return_to pointing at
	// the current page, so signing in brings the person back to the scene they were on.
	signInUrl?: string;
	// ACCOUNT_SUBSCRIBE target (defaults to /pricing). Not /checkout: that route is POST only and
	// needs a session, so the pricing page is the one a visitor can be sent to.
	pricingUrl?: string;
	// Whether the signed-in user owns this scene's record (owner-or-admin server-side). The engine
	// gates Ctrl+S / File→Save, File→Edit and the thumbnail upload on this; a non-owner can still
	// edit in-memory and Save As a fork. Defaults false (fail closed); becomes true after a create.
	currentUserIsSceneOwner?: boolean;
	// Site-wide admin role. Reveals the engine's error-reporting harness (DEBUG ▸ Error scenarios) and
	// nothing else — it grants no editing or saving power. Server-decided; defaults false.
	// See PLAN-wasm-error-reporting.md in the engine repo.
	currentUserIsAdmin?: boolean;
	// Whether anyone is signed in on this page (injected as data-current-user-is-signed-in). The
	// entitlements cannot answer it: an anonymous visitor and a signed-in free one are the same
	// grant, {"tier":"free","allows":[]}. The engine needs the difference for its refusal copy,
	// which says "sign in" to one and "subscribe" to the other. Grants nothing by itself.
	//
	// Forwarded to polygonAppInit the way currentUserIsAdmin is. An engine build without the option
	// ignores it, so the page needs no version check.
	currentUserIsSignedIn?: boolean;
	// What this visitor may do on the web, decided server-side (Entitlements::Grant#to_h, injected
	// as data-entitlements). `tier` is "free" or "web"; `allows` lists the actions that are open,
	// out of "live_api", "generative", "runtime_export", "model_export".
	//
	// A LIST rather than a map of booleans, so an engine that predates an action ignores the name
	// instead of reading `undefined` as false. Absent means free with nothing allowed, which is
	// also what the engine defaults to: fail closed at both ends. It decides what the editor
	// OFFERS, never what it is permitted to do. Every gate is enforced again server-side, so a
	// tampered attribute buys a menu item that refuses on click.
	//
	// Forwarded to polygonAppInit, which writes it into the wasm through `wasmEntitlements_WRITE`.
	// An engine build that predates that export ignores the option and stays on its own free
	// default, so the page needs no version check here.
	entitlements?: EntitlementsOption;
	// The live API relay, admin only (PLAN-live-api-agent.md). With this set, the host OFFERS the
	// engine a relay; the engine asks for it the first time the user opens the LLM panel, and only
	// then is an Action Cable channel subscribed, so a server-side agent can drive this tab. Unset on
	// every other page, and the engine's LLM panel then says "download the app" on its own.
	liveApiEnabled?: boolean;
	liveApiChannel?: string;
	// Where File ▸ Export For Web POSTs (PLAN-subscriptions.md D5). Set for the owner of a saved
	// scene; absent everywhere else, and the handler then says to save the scene first rather than
	// posting nowhere. The endpoint checks the subscription, the credits and the version again, so
	// this only decides whether the flow can START.
	exportUrl?: string;
	// Which scene this tab is on, so the subscription can name it. Undefined on /scenes/new.
	liveApiSceneUuid?: string;
	onLoadingProgress?: (snapshot: LoadingSnapshot) => void;
	// The viewer left or entered its chromeless fullscreen, so the editor bars just appeared or
	// disappeared. Only a host that did NOT give the engine the whole window has anything to do about
	// it: a scene embedded in a blog post is sized to its slot in the article, and the editor needs
	// more room than that slot. The engine cannot resize its own host, so it says what happened and
	// the page decides. Left undefined on the editor pages, which are already fullscreen.
	onViewerChromeChanged?: (chromeVisible: boolean) => void;
}

// The engine runs ONE AT A TIME per document: js-core keeps one render canvas on
// `globalThis.renderCanvas` and the wasm module has one APP_PTR. Since js-core 2.0.53 that is a
// lifecycle rather than a life sentence: `polygonAppInit` returns a PolygonAppController whose
// `dispose()` tears the whole engine down (render loop, GPU device, canvas, listeners, wasm heap),
// after which a fresh `loadEditor` boots cleanly in the same document. `unloadEditor` below is what
// a Turbo page calls on `turbo:before-cache`. `loadEditor` still returns silently while an engine
// is live.
let _controller: PolygonAppController | null = null;
// Set the moment loadEditor is called, cleared by unloadEditor. `_controller` cannot serve as the
// "an engine is claimed" flag any more: it only exists once the version's js-core has been fetched
// and polygonAppInit has run, and two boots racing inside that window would create two engines.
let _booting = false;
// The engine version the live instance is running, so an embed can reach the SAME bundle. Reading it
// off the resolver again would be correct but would also let an embed silently join a different
// version's module graph, which is the failure this whole file now exists to prevent.
let _polygonVersion: string | null = null;

// Resolves when the live engine has a renderer. A page that shows SEVERAL scenes boots the engine
// into the first one and attaches the rest to that same instance, so those have to be able to wait
// for it. They cannot create a canvas against a device that does not exist yet.
let _appReady: Promise<void> | null = null;
// The wasm exports of the live engine, needed to address its embeds. Set on onWasmLoaded, which
// runs before onRendererReady, so anything awaiting _appReady can rely on it.
let _appExportedFunctions: ExportedFunctions | undefined;
// Every extra scene attached to the live engine, so unloadEditor can take them down FIRST: their
// dispose calls into the wasm the engine's own dispose is about to remove.
const _embeds = new Set<EmbedHandle>();

// What an extra scene hands back. Named locally because the engine's own controller type carries
// more than this file touches, and `dispose` is all a host ever needs from one.
interface EmbedHandle {
	dispose: () => void;
}

// Whether an engine is currently live in this document. Exported because "returns silently"
// is only safe for a host that boots UNCONDITIONALLY. A host that boots on a condition, like a
// scene in a blog post booting when it scrolls into view, has to be able to ASK before it commits,
// or it will hide its poster and show a progress bar for a load that is never going to happen.
//
// True from the moment a boot is COMMITTED, not from the moment it finishes: the version's js-core
// has to be fetched first, and a caller that asked during that window and got `false` would boot a
// second engine into the same document.
export function editorLoaded(): boolean {
	return _booting || (_controller != null && !_controller.disposed);
}

// Tear the live engine down (no-op when none is live). The compiled wasm Module stays cached in
// js-core, so a later re-boot in this document pays instantiation, not download + compile.
export function unloadEditor(): void {
	for (const embed of _embeds) embed.dispose();
	_embeds.clear();
	// Before dispose: the relay holds a socket and a handle on the engine, and closing it first is
	// what lets the engine report `closed` with a reason rather than meet a dead carrier.
	disconnectLiveApiRelay();
	_controller?.dispose();
	_controller = null;
	_booting = false;
	_polygonVersion = null;
	_appReady = null;
	_appExportedFunctions = undefined;
}

export interface SceneEmbedOptions {
	// The element the embed's canvas is created inside and sized to, exactly like `domElement` for
	// the engine's own canvas.
	domElement: HTMLElement;
	sceneData: string; // inline `.poly`
}
export interface SceneEmbedController {
	dispose: () => void;
}

// Attach an ADDITIONAL scene to the live engine: its own scene graph, its own viewer, its own
// canvas, all inside the one wasm instance and on the one GPU device (PLAN-multiscenes-wasm.md
// phase 2, engine repo). This is what lets a post show several scenes without paying for several
// engines.
//
// Returns undefined, having created nothing, when this browser or this wasm build cannot do it:
// there is no WebGPU (embeds past the first are WebGPU-only), or the engine predates the embed
// exports. The caller keeps its poster in that case, which is the same working fallback it started
// with.
export async function loadSceneEmbed(
	options: SceneEmbedOptions,
): Promise<SceneEmbedController | undefined> {
	if (_appReady == null || _polygonVersion == null) {
		console.error("[editor] loadSceneEmbed: no engine to attach to");
		return undefined;
	}
	await _appReady;
	if (_appExportedFunctions == null) return undefined;

	// The LIVE engine's version, not the embed's. Every scene on the page runs inside one wasm
	// instance, so an embed has to reach the same module graph; blog.ts already refuses a figure that
	// pins a different version, and this is the other half of that rule.
	const runtime = await resolvePolygonRuntime(_polygonVersion);
	const webgpu = await runtime.webgpu();
	// An engine that predates the embed exports answers `undefined` here. Checked rather than called
	// blind: the site serves several engine versions at once, and "this version cannot do embeds" is a
	// state the caller already handles, since no WebGPU produces it too.
	if (webgpu.webGPUEmbedCreate == null) {
		console.error("[editor] loadSceneEmbed: this js-core has no embed support");
		return undefined;
	}
	const embed = webgpu.webGPUEmbedCreate({
		container: options.domElement,
		exportedFunctions: _appExportedFunctions,
		sceneData: options.sceneData,
		canvasTransparent: true,
	});
	if (embed == null) return undefined;

	_embeds.add(embed);
	return {
		dispose: () => {
			_embeds.delete(embed);
			embed.dispose();
		},
	};
}

// Boot the engine into `options.domElement`. ASYNCHRONOUS since PLAN-per-version-js-core.md: the
// first thing it does is fetch the js-core matching `options.polygonVersion`, and only then is there
// a `polygonAppInit` to call. Callers may ignore the promise. `editorLoaded()` is true from the
// moment the boot is committed, so a second call still returns silently, but awaiting it is how a
// caller learns the bundle could not be fetched at all.
export async function loadEditor(options: LoadEditorOptions): Promise<void> {
	if (editorLoaded()) return;
	_booting = true;
	_polygonVersion = options.polygonVersion;

	let exportedFunctions: ExportedFunctions | undefined;
	let readyResolve: () => void = () => {};
	// Created BEFORE the await, because loadSceneEmbed waits on it: a second scene on the page can
	// ask to attach while the first one's js-core is still downloading.
	_appReady = new Promise<void>((resolve) => (readyResolve = resolve));

	// The js-core that matches this scene's wasm. A failure here is a page that cannot boot at all:
	// there is no engine to degrade to, so it gives the claim back and says so, leaving the caller's
	// poster or progress bar to be its own fallback rather than a document stuck on "loading".
	let runtime;
	try {
		runtime = await resolvePolygonRuntime(options.polygonVersion);
	} catch (error) {
		_booting = false;
		_polygonVersion = null;
		_appReady = null;
		console.error(`[editor] could not load the js-core for polygon ${options.polygonVersion}`, error);
		reportHostError(error, { polygonVersion: options.polygonVersion, phase: "resolvePolygonRuntime" });
		return;
	}
	// Torn down while the bundle was downloading: a Turbo navigation away from a blog post is the
	// real case. Booting now would create an engine that nothing holds a handle to and nothing will
	// ever dispose.
	if (!_booting) return;
	const { polygonAppInit } = runtime.core;
	// The engine's own failures reach Sentry only through a sink this js-core exposes, so it is
	// registered as soon as there is a js-core and before anything is fetched through it.
	registerEngineErrorSink(runtime.core);

	// Select the versioned securewasm endpoint: /polygon-wasm/<version>/<kind>/…
	// Only the "editor" kind exists: the "runtime" (/play) kind was removed until a dedicated
	// export-stripped polygonjs.com runtime is built — the engine's generic runtime is the canvas
	// web-export build, which carries file export and must never be served here. So /play currently
	// loads the editor wasm too (it isn't routed to yet). The bytes arrive via the securewasm
	// handshake and are intercepted on wasmURL, so wasmRoot/wasmFilePrefix only need to be internally
	// consistent with initUrl — wasmFilePrefix is any stable basename.
	const kind = "editor";
	const wasmRoot = `/polygon-wasm/${options.polygonVersion}/${kind}/`;

	_controller = polygonAppInit<ExportedFunctions>({
		wasmRoot,
		assetsRoot: "/",
		wasmFilePrefix: "wasm",
		assetsVersion: import.meta.env.VITE_BUILD_HASH,
		secureWasm: { initUrl: `${wasmRoot}init` },
		sceneData: options.sceneData,
		sceneName: options.sceneName,
		autoplay: options.autoplay,
		exampleId: options.exampleId,
		currentUserIsSceneOwner: options.currentUserIsSceneOwner,
		currentUserIsAdmin: options.currentUserIsAdmin,
		currentUserIsSignedIn: options.currentUserIsSignedIn,
		entitlements: options.entitlements,
		editorDisabled: options.editorDisabled,
		domElement: options.domElement,
		// if we want any object to follow the cursor when the canvas is not fullscreen,
		// it's best to listen to the whole body, otherwise the events outside are not registered,
		// and objects positions clamp on the edges
		domElementEventsListener: document.body,
		//
		useHotReloadInDev: true,
		fullscreen: options.fullscreen ?? true,
		inIFrame: false,
		// Every subpath URL composed from the same version base as the module above, so the editor,
		// io, overlay, video, webcam and renderer chunks can only ever come from ONE bundle.
		loaders: runtime.loaders,
		canvasTransparent: true,
		onWasmLoaded: (functions) => {
			exportedFunctions = functions;
			_appExportedFunctions = functions;
		},
		onRendererReady: () => {
			readyResolve();
			return Promise.resolve();
		},
		onWebGPUNotSupported: () => {
			console.error("[editor] webgpu not supported");
		},
		onLoadingProgress: options.onLoadingProgress,
		onEditorAction: (payload: EditorActionPayload) => {
			if (exportedFunctions == null) {
				return;
			}
			void handleEditorAction(payload, options, exportedFunctions, runtime.core);
		},
	});

	// The relay OFFER, on the controller polygonAppInit just returned. It cannot be registered yet:
	// the live API arrives with the editor chunk, which resolves after this call, so
	// registerLiveApiRelay waits for it. Called unconditionally; it returns at once when the page
	// offers no relay, which is every page that is not an admin's. Registering opens nothing: the
	// engine asks for the transport when the user opens the LLM panel.
	registerLiveApiRelay({
		// `liveApi` is optional on LiveApiRelayHost and liveApiRelay.ts checks for it at runtime,
		// because an engine version older than the relay simply does not have it. That check is the
		// degradation; nothing here needs a cast for it any more.
		controller: _controller as LiveApiRelayHost,
		enabled: options.liveApiEnabled === true,
		channel: options.liveApiChannel ?? "",
		// A GETTER, not the value: this runs at boot, and a tab that booted on /scenes/new gets its
		// uuid later, when the first save creates the record (createScene below). The relay reads it
		// when the engine asks for a transport, which is whenever the LLM panel is first opened.
		sceneUuid: () => options.liveApiSceneUuid,
	});
}

// The engine→host actions this page handles, by their stable wire NAME rather than js-core's
// `EditorAction` enum. The enum's values are declaration order, and this page is built once and then
// drives every engine version a scene may pin, so an insertion into the engine's enum shifts every
// later value under an already-deployed bundle, silently. That has happened: engine commit fcd77a5b9
// added SCENE_NEW at 1 and pushed SCENE_SAVE from 1 to 2.
//
// A LITERAL LIST rather than the engine's `EDITOR_ACTION_WIRE_NAMES` table, deliberately. That table
// is a VALUE, and importing a value from `@polygonjs/js-core` is exactly what this file no longer
// does: the js-core in the page is the one resolved for the scene's version, and a build-time import
// would bake a second, possibly different, copy of the engine into the bundle. `satisfies
// readonly EditorActionName[]` keeps the check without the import: a name the engine does not
// declare fails the typecheck, against the PINNED js-core's types (package.json, types only).
//
// THAT CHECK ERRORS WHILE THE ENGINE IS AHEAD OF THE PIN, AND THAT IS THE INTENDED BEHAVIOUR. The
// pin sits one tag behind on purpose, so a name added here for an unreleased engine has nowhere to
// be declared yet and tsc says so, naming this array. The error is the transient part, not the code:
// it clears itself on the version bump, with nothing here to rewrite. Actions newer than the pin
// used to live OUTSIDE this list as loose string constants checked ahead of it, precisely to keep
// tsc quiet, each with a comment asking some future reader to move it back in. That traded a loud,
// self-clearing error for a silent second mechanism nobody was obliged to collapse, and
// `scene.exportWebApp` sat in it for exactly that reason. One list, one check, one noisy interval.
//
// Independently of js-core, `as const` makes HandledAction a union of these literals, so the switch
// below is exhaustive through assertNever, and an unknown name is refused by isHandledAction at
// runtime. The engine's wire names are the contract here; they are stable strings by design
// (EditorAction.ts, EDITOR_ACTION_WIRE_NAMES), never the enum's numbers, which shift whenever an
// action is inserted.
//
// This is a SET, not an ordered table: entries may be added, removed or reordered freely. Append-only
// is a rule about the ENGINE's wire names, where the string is the identity, not about this list.
const HANDLED_ACTION_NAMES = [
	"nav.dashboard",
	"nav.edit",
	"scene.new",
	"scene.save",
	"scene.saveAs",
	"scene.screenshotCaptured",
	"viewer.chromeShown",
	"viewer.chromeHidden",
	"scene.exportWebApp",
	"account.signIn",
	"account.subscribe",
] as const satisfies readonly EditorActionName[];

// Where the editor is now, as a same-origin path, for the sign-in form to return to.
// SessionsController#new stores it and after_authentication_url redirects there.
function signInUrlWithReturnTo(base: string): string {
	const returnTo = window.location.pathname + window.location.search;
	const separator = base.includes("?") ? "&" : "?";
	return `${base}${separator}return_to=${encodeURIComponent(returnTo)}`;
}

type HandledAction = (typeof HANDLED_ACTION_NAMES)[number];
const HANDLED_ACTIONS: ReadonlySet<string> = new Set(HANDLED_ACTION_NAMES);

function isHandledAction(name: string): name is HandledAction {
	return HANDLED_ACTIONS.has(name);
}

// Exhaustiveness over the set we claim to handle. js-core exports a TypeAssert that does exactly
// this, but importing it would keep this file on the engine's type surface for three lines of code.
function assertNever(value: never): never {
	throw new Error(`unhandled editor action: ${String(value)}`);
}

// Map an engine editor action onto Rails:
//   scene.save     → PATCH the existing scene in place, no reload (on /scenes/new: first save creates).
//   scene.saveAs   → POST a new scene, then navigate to it.
//   nav.dashboard  → navigate to the dashboard.
//   account.*      → navigate to the sign-in form or the pricing page, in the same tab.
async function handleEditorAction(
	payload: EditorActionPayload,
	options: LoadEditorOptions,
	exportedFunctions: ExportedFunctions,
	core: PolygonCoreModule,
): Promise<void> {
	// The name the ENGINE sent. Every version this site hosts carries `actionName`, so there is no
	// integer decoding left anywhere: the enum's numbers mean different things in different versions,
	// which is the whole reason the names exist.
	const action = payload.actionName;
	if (!isHandledAction(action)) {
		// A NEWER engine than this deploy was built against can emit an action we have no handler
		// for. Once one page serves many engine versions that is an ordinary event, not a bug, so it
		// is a log and not a throw. The engine treats an ignored action as the host declining it.
		console.warn(`[editor] ignoring unknown editor action "${action}" (this page predates it)`);
		return;
	}
	switch (action) {
		case "nav.dashboard": {
			// File→Dashboard / File→New open in a NEW TAB so the current scene stays put.
			window.open(options.dashboardUrl ?? "/dashboard", "_blank");
			return;
		}
		case "scene.new": {
			window.open(options.scenesNewUrl ?? "/scenes/new", "_blank");
			return;
		}
		case "scene.saveAs": {
			await createScene(payload, options, exportedFunctions, core);
			return;
		}
		case "scene.save": {
			if (options.saveUrl) {
				await updateScene(payload, options.saveUrl);
			} else {
				await createScene(payload, options, exportedFunctions, core);
			}
			return;
		}
		case "nav.edit": {
			window.open(options.sceneEditUrl ?? "/", "_blank");
			return;
		}
		case "scene.screenshotCaptured": {
			if (options.saveUrl) {
				await updateScreenshot(payload, options.saveUrl);
			} else {
				alert("Please save your scene before updating its screenshot.");
			}
			return;
		}
		case "viewer.chromeShown": {
			options.onViewerChromeChanged?.(true);
			return;
		}
		case "viewer.chromeHidden": {
			options.onViewerChromeChanged?.(false);
			return;
		}
		case "scene.exportWebApp": {
			await startWebAppExport({ exportUrl: options.exportUrl });
			return;
		}
		// The agent panel's account upsell: "Sign in" for a signed-out visitor, "Subscribe" for a
		// signed-in one without the live API. Both LEAVE the editor on purpose, in the SAME TAB, which
		// is why the engine sends an action instead of opening a URL itself: a sign-in that lands in a
		// second tab cannot hand the person back to the scene they came from.
		case "account.signIn": {
			window.location.assign(signInUrlWithReturnTo(options.signInUrl ?? "/session/new"));
			return;
		}
		case "account.subscribe": {
			window.location.assign(options.pricingUrl ?? "/pricing");
			return;
		}
		default: {
			assertNever(action);
		}
	}
}

// New scene / Save As: POST (poly + optional screenshot + name). On success we do NOT reload —
// we swap the address bar to the created scene's URL (keeps the editor mounted, no wasm re-init)
// and switch to update-in-place for subsequent saves. `data.url` is the canonical
// /:user/:scenename path, which is BOTH the page URL (GET) and the PATCH target. The server
// auto-suffixes a taken name (name → name1 → name2 …), so data.name is the name actually used.
async function createScene(
	payload: EditorActionPayload,
	options: LoadEditorOptions,
	exportedFunctions: ExportedFunctions,
	core: PolygonCoreModule,
): Promise<void> {
	if (!options.createUrl) {
		console.error(
			"[editor] no create URL on this page — cannot save a new scene",
		);
		return;
	}
	const fd = saveFormData(payload, true); // keep the initial thumbnail on creation
	fd.append("scene[name]", payload.sceneName);
	const res = await fetch(options.createUrl, {
		method: "POST",
		body: fd,
		headers: jsonHeaders(),
		credentials: "same-origin",
	});
	if (res.ok) {
		const data = (await res.json()) as {
			url: string;
			name: string;
			username: string;
			uuid?: string;
			export_url?: string;
		};
		history.replaceState({}, "", data.url);
		options.saveUrl = data.url; // subsequent SCENE_SAVE now updates in place
		options.sceneEditUrl = `${data.url}/edit`; // File→Edit now targets the created scene
		// The two addresses that only exist once the record does. The page was rendered from
		// /scenes/new, which has no scene and therefore emits neither data-export-url nor
		// data-scene-uuid, so without this the tab keeps a pre-save view of itself until a reload:
		// File ▸ Export For Web would say to save a scene that has just been saved, and the live API
		// would subscribe with no scene. Optional on the response so an older server (or a fixture
		// that predates them) leaves what was there rather than blanking it.
		if (data.export_url) options.exportUrl = data.export_url;
		if (data.uuid) options.liveApiSceneUuid = data.uuid;
		document.title = `${data.username}/${data.name}`; // matches the server-side scene title
		// The signed-in user now owns the freshly created record (this is the Save-As-a-fork case
		// too: started as a non-owner, now owns the fork), so lift the engine's save/edit gate.
		options.currentUserIsSceneOwner = true;
		if (exportedFunctions?.wasmSceneOwner_WRITE) {
			exportedFunctions.wasmSceneOwner_WRITE(1);
		}
		if (exportedFunctions?.wasmSceneName_WRITE) {
			// in case the requested scene name was already taken, it was increased server side,
			// and we give the persisted one to the engine here
			const { ptr, size } = core.internalApi.requestAndWriteStringToMemory(
				data.name,
			);
			exportedFunctions.wasmSceneName_WRITE(ptr, size);
		}
	} else {
		await logSaveError(res);
	}
}

// Existing scene: PATCH the `.poly` in place (no screenshot — that's SCREENSHOT_CAPTURED's job),
// no navigation — no lost work.
async function updateScene(
	payload: EditorActionPayload,
	saveUrl: string,
): Promise<void> {
	const res = await fetch(saveUrl, {
		method: "PATCH",
		body: saveFormData(payload, false),
		headers: jsonHeaders(),
		credentials: "same-origin",
	});
	if (!res.ok) await logSaveError(res);
}

async function updateScreenshot(
	payload: EditorActionPayload,
	saveUrl: string,
): Promise<void> {
	if (!payload.screenshot) return;
	const fd = new FormData();
	fd.append("scene[screenshot]", payload.screenshot, "screenshot.png");
	const res = await fetch(saveUrl, {
		method: "PATCH",
		body: fd,
		headers: jsonHeaders(),
		credentials: "same-origin",
	});
	if (!res.ok) await logSaveError(res);
}

// Build the save body. A NEW scene keeps the screenshot the payload carries (so it gets an
// initial thumbnail). A persisted scene's SAVE persists the `.poly` ONLY — its thumbnail is
// updated exclusively through SCREENSHOT_CAPTURED (updateScreenshot), so it changes only when
// the engine deliberately captures one.
function saveFormData(
	payload: EditorActionPayload,
	withScreenshot: boolean,
): FormData {
	const fd = new FormData();
	fd.append("scene[poly]", payload.poly ?? "");
	if (withScreenshot && payload.screenshot) {
		fd.append("scene[screenshot]", payload.screenshot, "screenshot.png");
	}
	return fd;
}

function jsonHeaders(): HeadersInit {
	return { Accept: "application/json", "X-CSRF-Token": csrfToken() };
}

function csrfToken(): string {
	return (
		document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
			?.content ?? ""
	);
}

async function logSaveError(res: Response): Promise<void> {
	if (res.status === 401) {
		console.warn("[editor] save rejected (401): sign in to save.");
		return;
	}
	let message = `${res.status}`;
	try {
		const body = (await res.json()) as { errors?: string[] };
		if (body?.errors?.length) message = body.errors.join(", ");
	} catch {
		// non-JSON error body; keep the status
	}
	console.error(`[editor] save failed: ${message}`);
}
